import { toDate } from "date-fns-tz";
import { TFunction } from "i18next";

import { EntityAttribute, VehicleAgeTimeline } from "shared/api/api";
import { Granularity, granularityOptions } from "shared/api/constants";
import {
  DEFAULT_BY_VEHICLE_AGE,
  DEFAULT_EXPOSURE,
  NONE_EXPOSURE,
} from "shared/constants";
import { TOP_CONTRIBUTORS_GROUP_BY_ACCESSOR } from "shared/schemas/constants";
import { sortByValue } from "shared/schemas/utils/schema-utils";
import { DataType, EventTypeEnum, PopulationLine } from "shared/types";

import {
  getMeasureLabel,
  getTotalEventsOptionId,
} from "features/ui/charts/actions";
import {
  ChartAction,
  SelectedChartOptions,
} from "features/ui/charts/Actions/types";
import { GRAPH_STROKE_WIDTH } from "features/ui/charts/constants";
import { DataElement } from "features/ui/charts/types";
import {
  getAxisLabel,
  getAxisValue,
  getColor,
  getDefaultActions,
  getSelectedOptionId,
} from "features/ui/charts/utils";
import { PageChartSettingsState } from "features/ui/Filters/types";
import { RowData, SchemaEntry } from "features/ui/MaterialTable";
import { SelectOption } from "features/ui/Select";
import { ActionType } from "features/ui/StepperModal";

import { MONTHS_IN_SERVICE_EXPOSURE_BUCKET } from "./constants";
import { NONE_EXPOSURE_ID } from "./shared/analytics/constants";
import { ByVehicleAgeData } from "./types";

/**
 * Analytics pages: By Production Date utils
 */

export const getByVehicleAgeChartOptions = (
  yAxisOptions: SelectOption[],
  xAxisOptions: SelectOption[],
  exposures: SelectOption[],
  t: TFunction
): ChartAction[] => [
  {
    id: "y",
    title: getMeasureLabel(t),
    type: "dropdownSelect",
    options: yAxisOptions,
    defaultOptionId: getTotalEventsOptionId(yAxisOptions),
  },
  {
    id: "x",
    title: t("analytics.x_axis"),
    type: "dropdownSelect",
    options: xAxisOptions,
    defaultOptionId: DEFAULT_BY_VEHICLE_AGE,
  },
  {
    id: "granularity",
    title: t("analytics.granularity"),
    type: "dropdownSelect",
    options: granularityOptions(t),
    defaultOptionId: Granularity.WEEK,
  },
  {
    id: "exposure",
    title: t("analytics.exposure"),
    type: "dropdownSelect",
    options: exposures,
    defaultOptionId: DEFAULT_EXPOSURE,
  },
  {
    id: "logarithmic",
    title: t("analytics.logarithmic_scale"),
    type: "boolean",
    defaultOptionId: "false",
  },
];

export const areThereAnyByAgeAttributes = (
  vehicleAttributes?: EntityAttribute[],
  resourceAttributes?: EntityAttribute[],
  allowNoneExposure?: boolean
) =>
  vehicleAttributes &&
  vehicleAttributes.length > 0 &&
  vehicleAttributes.find(({ byVehicleAgeBirthday }) => byVehicleAgeBirthday) &&
  ((resourceAttributes &&
    resourceAttributes.length > 0 &&
    resourceAttributes.find(
      ({ byVehicleAgeExposure }) => byVehicleAgeExposure
    )) ||
    allowNoneExposure);

export const isSelectedByAgeAttributeInvalid = (
  byVehicleAgeData: ByVehicleAgeData,
  vehicleAttributes?: EntityAttribute[],
  resourceAttributes?: EntityAttribute[],
  allowNoneExposure?: boolean
) =>
  (byVehicleAgeData.byVehicleAgeBirthday &&
    vehicleAttributes &&
    vehicleAttributes.length > 0 &&
    !vehicleAttributes.find(
      ({ ID, byVehicleAgeBirthday }) =>
        ID === byVehicleAgeData.byVehicleAgeBirthday && byVehicleAgeBirthday
    )) ||
  (byVehicleAgeData.byVehicleAgeExposure &&
    resourceAttributes &&
    resourceAttributes.length > 0 &&
    !resourceAttributes.find(
      ({ ID, byVehicleAgeExposure }) =>
        ID === byVehicleAgeData.byVehicleAgeExposure && byVehicleAgeExposure
    ) &&
    !allowNoneExposure);

export const generateByVehicleAgeYAxisLines = (
  data: VehicleAgeTimeline[],
  byVehicleAgeData: ByVehicleAgeData,
  reverseOrder: boolean = false
): PopulationLine[] => {
  const { yAxisKey, yAxisValue, byVehicleAgeBirthday, byVehicleAgeExposure } =
    byVehicleAgeData;
  const uniqueIDs = Array.from(
    new Set(data.map(({ exposureBucket }) => exposureBucket))
  ).sort((a, b) => (reverseOrder ? b - a : a - b));

  return uniqueIDs.map((exposureBucket: number, index: number) => ({
    color: getColor(index),
    label:
      byVehicleAgeExposure !== NONE_EXPOSURE_ID
        ? exposureBucket.toString()
        : yAxisValue,
    dashed: false,
    key: `${yAxisKey}-${byVehicleAgeBirthday}-${exposureBucket}`,
    width: GRAPH_STROKE_WIDTH,
    dot: true,
  }));
};

export const toTimestamp = (dateString: string): number =>
  toDate(dateString).getTime();

export const prepareByVehicleAgeDataByTimestamp = (
  data: VehicleAgeTimeline[] | undefined,
  byVehicleAgeData: ByVehicleAgeData
): DataElement[] => {
  if (!data) {
    return [];
  }

  const { yAxisKey, byVehicleAgeBirthday } = byVehicleAgeData;

  return (
    data
      .map((data) => {
        const { birthdayBucket, exposureBucket, ...otherData } = data;
        const keyYAxis = `${yAxisKey}-${byVehicleAgeBirthday}-${exposureBucket}`;

        const ts = toTimestamp(birthdayBucket);

        return {
          [keyYAxis]: data[yAxisKey as keyof VehicleAgeTimeline],
          ts,
          exposureBucket,
          [exposureBucket]: otherData,
          byVehicleAgeData,
        };
      })
      // we would like to have only one entry per timestamp, so recharts does not go mad
      .reduce((acc: DataElement[], value: DataElement) => {
        const i = acc.findIndex(({ ts }) => ts === value.ts);
        if (i > -1) {
          acc[i] = { ...acc[i], ...value };
        } else {
          acc.push(value);
        }

        return acc;
      }, [])
  );
};

const prepareClaimsVehiclesByAgeTableSchemaAndData = (
  data: VehicleAgeTimeline[],
  showCosts: boolean,
  granularity: Granularity,
  buildDateLabel: string,
  exposure: string,
  t: TFunction
) => {
  const newData: RowData[] = [];
  const exposureList: number[] = [];

  data.forEach(
    ({
      birthdayBucket,
      cumulativeIPTV,
      cumulativeCPV,
      numVehicles,
      numEvents,
      exposureBucket,
      distinctIPTV,
      vehiclesAtExposure,
      percentVehiclesAtExposure,
      totalVehicles,
      costRate,
      FRCPercent,
    }) => {
      exposureList.push(exposureBucket);

      const currentRow = newData.find(
        (row) => row.birthdayBucket === birthdayBucket
      );

      if (currentRow) {
        currentRow[`FRCPercent_${exposureBucket}`] = FRCPercent;
        currentRow[`IPTV_${exposureBucket}`] = cumulativeIPTV;
        currentRow[`DIPTV_${exposureBucket}`] = distinctIPTV;
        currentRow[`costRate_${exposureBucket}`] = costRate;
        currentRow[`numEvents_${exposureBucket}`] = numEvents;
        currentRow[`numVehicles_${exposureBucket}`] = numVehicles;
        currentRow[`CPV_${exposureBucket}`] = cumulativeCPV;
        currentRow[`vehiclesAtExposure_${exposureBucket}`] = vehiclesAtExposure;
        currentRow[`totalVehicles_${exposureBucket}`] = totalVehicles;
        currentRow[`percentVehiclesAtExposure_${exposureBucket}`] =
          percentVehiclesAtExposure;
      } else {
        newData.push({
          birthdayBucket,
          [`FRCPercent_${exposureBucket}`]: FRCPercent,
          [`IPTV_${exposureBucket}`]: cumulativeIPTV,
          [`DIPTV_${exposureBucket}`]: distinctIPTV,
          [`costRate_${exposureBucket}`]: costRate,
          [`numEvents_${exposureBucket}`]: numEvents,
          [`numVehicles_${exposureBucket}`]: numVehicles,
          [`CPV_${exposureBucket}`]: cumulativeCPV,
          [`vehiclesAtExposure_${exposureBucket}`]: vehiclesAtExposure,
          [`totalVehicles_${exposureBucket}`]: totalVehicles,
          [`percentVehiclesAtExposure_${exposureBucket}`]:
            percentVehiclesAtExposure,
        });
      }
    }
  );

  const uniqueExposureValues = new Set(exposureList);
  const exposureValuesArray = Array.from(uniqueExposureValues).sort(
    (a, b) => a - b
  );

  const skipExposureColumns = exposure === NONE_EXPOSURE_ID;

  const exposureSchemaEntries = exposureValuesArray
    .map((exposureBucket) =>
      [
        {
          accessor: `FRCPercent_${exposureBucket}`,
          label: t("analytics.by_age.frc_label"),
          description: t("analytics.by_age.frc_description"),
          dataType: DataType.PERCENTAGE,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `IPTV_${exposureBucket}`,
          label: t("analytics.by_age.estimated_events_label"),
          description: t("analytics.by_age.estimated_events_description"),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `DIPTV_${exposureBucket}`,
          label: t("analytics.by_age.estimated_affected_label"),
          description: t("analytics.by_age.estimated_affected_description"),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        showCosts && {
          accessor: `costRate_${exposureBucket}`,
          label: t("analytics.by_age.cpv_label"),
          description: t("analytics.by_age.cpv_rate_description"),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `numEvents_${exposureBucket}`,
          label: t("analytics.by_age.incident_count_label"),
          description: t("analytics.by_age.incident_count_description"),
          dataType: DataType.NUMBER,
        },
        {
          accessor: `numVehicles_${exposureBucket}`,
          label: t("analytics.by_age.affected_vehicles_label"),
          description: t("analytics.by_age.affected_vehicles_description"),
          dataType: DataType.NUMBER,
        },
        showCosts && {
          accessor: `CPV_${exposureBucket}`,
          label: t("analytics.by_age.cost_label"),
          description: t("analytics.by_age.cost_description"),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        !skipExposureColumns && {
          accessor: `vehiclesAtExposure_${exposureBucket}`,
          label: t("analytics.by_age.vehicles_at_mis_label"),
          description: t("analytics.by_age.vehicles_at_mis_description"),
          dataType: DataType.NUMBER,
        },
        {
          accessor: `totalVehicles_${exposureBucket}`,
          label: t("analytics.by_age.vehicles_manufactured_label"),
          description: t("analytics.by_age.vehicles_manufactured_description"),
          dataType: DataType.NUMBER,
        },
        !skipExposureColumns && {
          accessor: `percentVehiclesAtExposure_${exposureBucket}`,
          label: t("analytics.by_age.vehicles_at_mis_percent_label"),
          description: t(
            "analytics.by_age.vehicles_at_mis_percent_description"
          ),
          dataType: DataType.NUMBER,
        },
      ].filter(Boolean)
    )
    .flat() as SchemaEntry[];

  const schema: SchemaEntry[] = [
    {
      accessor: "birthdayBucket",
      label: buildDateLabel,
      dataType:
        granularity === Granularity.MONTH
          ? DataType.DATE_YEAR_MONTH
          : DataType.DATE_YEAR_MONTH_DAY,
    },
    ...exposureSchemaEntries,
  ];

  return {
    data: newData,
    schema,
    uniqueExposureValues,
  };
};

const prepareSignalEventVehiclesByAgeTableSchemaAndData = (
  data: VehicleAgeTimeline[],
  granularity: Granularity,
  buildDateLabel: string,
  exposure: string,
  t: TFunction
) => {
  const newData: RowData[] = [];
  const exposureList: number[] = [];

  data.forEach(
    ({
      birthdayBucket,
      cumulativeIPTV,
      cumulativeDPTV,
      numVehicles,
      numEvents,
      exposureBucket,
      vehiclesAtExposure,
      percentVehiclesAtExposure,
      totalVehicles,
      FRCPercent,
    }) => {
      exposureList.push(exposureBucket);

      const currentRow = newData.find(
        (row) => row.birthdayBucket === birthdayBucket
      );

      if (currentRow) {
        currentRow[`FRCPercent_${exposureBucket}`] = FRCPercent;
        currentRow[`cumulativeDPTV_${exposureBucket}`] = cumulativeDPTV;
        currentRow[`cumulativeIPTV_${exposureBucket}`] = cumulativeIPTV;
        currentRow[`numEvents_${exposureBucket}`] = numEvents;
        currentRow[`numVehicles_${exposureBucket}`] = numVehicles;
        currentRow[`vehiclesAtExposure_${exposureBucket}`] = vehiclesAtExposure;
        currentRow[`totalVehicles_${exposureBucket}`] = totalVehicles;
        currentRow[`percentVehiclesAtExposure_${exposureBucket}`] =
          percentVehiclesAtExposure;
      } else {
        newData.push({
          birthdayBucket,
          [`FRCPercent_${exposureBucket}`]: FRCPercent,
          [`cumulativeDPTV_${exposureBucket}`]: cumulativeDPTV,
          [`cumulativeIPTV_${exposureBucket}`]: cumulativeIPTV,
          [`numEvents_${exposureBucket}`]: numEvents,
          [`numVehicles_${exposureBucket}`]: numVehicles,
          [`vehiclesAtExposure_${exposureBucket}`]: vehiclesAtExposure,
          [`totalVehicles_${exposureBucket}`]: totalVehicles,
          [`percentVehiclesAtExposure_${exposureBucket}`]:
            percentVehiclesAtExposure,
        });
      }
    }
  );

  const uniqueExposureValues = new Set(exposureList);
  const exposureValuesArray = Array.from(uniqueExposureValues).sort(
    (a, b) => a - b
  );

  const skipExposureColumns = exposure === NONE_EXPOSURE_ID;

  const exposureSchemaEntries = exposureValuesArray
    .map((exposureBucket) =>
      [
        {
          accessor: `FRCPercent_${exposureBucket}`,
          label: t("analytics.by_age.frc_label"),
          description: t("analytics.by_age.frc_description"),
          dataType: DataType.PERCENTAGE,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `cumulativeDPTV_${exposureBucket}`,
          label: t("analytics.by_age.estimated_affected_label"),
          description: t("analytics.by_age.estimated_affected_description"),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `cumulativeIPTV_${exposureBucket}`,
          label: t("analytics.by_age.estimated_events_label"),
          description: t(
            "analytics.by_age.estimated_events_signal_description"
          ),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `numEvents_${exposureBucket}`,
          label: t("event_type.event_other"),
          description: t("analytics.by_age.events_description"),
          dataType: DataType.NUMBER,
        },
        {
          accessor: `numVehicles_${exposureBucket}`,
          label: t("analytics.by_age.affected_vehicles_label"),
          description: t("analytics.by_age.affected_vehicles_description"),
          dataType: DataType.NUMBER,
        },
        !skipExposureColumns && {
          accessor: `vehiclesAtExposure_${exposureBucket}`,
          label: t("analytics.by_age.vehicles_at_mis_label"),
          description: t("analytics.by_age.vehicles_at_mis_description"),
          dataType: DataType.NUMBER,
        },
        {
          accessor: `totalVehicles_${exposureBucket}`,
          label: t("analytics.by_age.vehicles_manufactured_label"),
          description: t("analytics.by_age.vehicles_manufactured_description"),
          dataType: DataType.NUMBER,
        },
        !skipExposureColumns && {
          accessor: `percentVehiclesAtExposure_${exposureBucket}`,
          label: t("analytics.by_age.vehicles_at_mis_percent_label"),
          description: t(
            "analytics.by_age.vehicles_at_mis_percent_description"
          ),
          dataType: DataType.NUMBER,
        },
      ].filter(Boolean)
    )
    .flat() as SchemaEntry[];

  const schema: SchemaEntry[] = [
    {
      accessor: "birthdayBucket",
      label: buildDateLabel,
      dataType:
        granularity === Granularity.MONTH
          ? DataType.DATE_YEAR_MONTH
          : DataType.DATE_YEAR_MONTH_DAY,
    },
    ...exposureSchemaEntries,
  ];

  return {
    data: newData,
    schema,
    uniqueExposureValues,
  };
};

export const prepareVehiclesByAgeTableSchemaAndData = (
  data: VehicleAgeTimeline[],
  showCosts: boolean,
  granularity: Granularity,
  buildDateLabel: string,
  entity: EventTypeEnum,
  exposure: string,
  t: TFunction
) => {
  if (entity === EventTypeEnum.CLAIM) {
    return prepareClaimsVehiclesByAgeTableSchemaAndData(
      data,
      showCosts,
      granularity,
      buildDateLabel,
      exposure,
      t
    );
  }

  if (entity === EventTypeEnum.SIGNAL_EVENT) {
    return prepareSignalEventVehiclesByAgeTableSchemaAndData(
      data,
      granularity,
      buildDateLabel,
      exposure,
      t
    );
  }

  const newData: RowData[] = [];
  const exposureList: number[] = [];

  // rewrite the below using reduce to make it nicer..
  data.forEach(
    ({
      birthdayBucket,
      cumulativeIPTV,
      cumulativeCPV,
      numVehicles,
      numEvents,
      exposureBucket,
      FRCPercent,
    }) => {
      exposureList.push(exposureBucket);

      const currentRow = newData.find(
        (row) => row.birthdayBucket === birthdayBucket
      );

      if (currentRow) {
        currentRow[`FRCPercent_${exposureBucket}`] = FRCPercent;
        currentRow[`IPTV_${exposureBucket}`] = cumulativeIPTV;
        currentRow[`CPV_${exposureBucket}`] = cumulativeCPV;
        currentRow[`numVehicles_${exposureBucket}`] = numVehicles;
        currentRow[`numEvents_${exposureBucket}`] = numEvents;
      } else {
        newData.push({
          birthdayBucket,
          [`FRCPercent_${exposureBucket}`]: FRCPercent,
          [`IPTV_${exposureBucket}`]: cumulativeIPTV,
          [`CPV_${exposureBucket}`]: cumulativeCPV,
          [`numVehicles_${exposureBucket}`]: numVehicles,
          [`numEvents_${exposureBucket}`]: numEvents,
        });
      }
    }
  );

  const uniqueExposureValues = new Set(exposureList);
  const exposureValuesArray = Array.from(uniqueExposureValues).sort(
    (a, b) => a - b
  );

  const exposureSchemaEntries = exposureValuesArray
    .map((exposureBucket) =>
      [
        {
          accessor: `FRCPercent_${exposureBucket}`,
          label: t("analytics.by_age.frc_label"),
          dataType: DataType.PERCENTAGE,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `IPTV_${exposureBucket}`,
          label: t("analytics.by_age.iptv_label"),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        showCosts && {
          accessor: `CPV_${exposureBucket}`,
          label: t("analytics.by_age.cpv_label"),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `numVehicles_${exposureBucket}`,
          label: t("analytics.by_age.total_vehicles_label"),
          dataType: DataType.NUMBER,
        },
        {
          accessor: `numEvents_${exposureBucket}`,
          label: t(`common:event_type.${entity.toLowerCase()}`, { count: 2 }),
          dataType: DataType.NUMBER,
        },
      ].filter(Boolean)
    )
    .flat() as SchemaEntry[];

  const schema: SchemaEntry[] = [
    {
      accessor: "birthdayBucket",
      label: buildDateLabel,
      dataType:
        granularity === Granularity.MONTH
          ? DataType.DATE_YEAR_MONTH
          : DataType.DATE_YEAR_MONTH_DAY,
    },
    ...exposureSchemaEntries,
  ];

  return {
    data: newData,
    schema,
    uniqueExposureValues,
  };
};

/**
 * Analytics pages: Top Contributors utils
 */
export const prepareTopContributorsData = <
  T extends { groupByAttributeValue: string | number | boolean | null },
>(
  data: T[],
  attribute: keyof T
) =>
  data
    .map((tc) => ({
      dimension: tc[attribute],
      // we use .toString() so that boolean values are presented correctly in the BarChart
      [TOP_CONTRIBUTORS_GROUP_BY_ACCESSOR]:
        tc[TOP_CONTRIBUTORS_GROUP_BY_ACCESSOR] === null
          ? null
          : tc[TOP_CONTRIBUTORS_GROUP_BY_ACCESSOR].toString(),
    }))
    .sort(({ dimension: dimensionA }, { dimension: dimensionB }) => {
      // nulls sort after anything else
      if (dimensionA === null) {
        return 1;
      }

      if (dimensionB === null) {
        return -1;
      }

      if (typeof dimensionA === "string" && typeof dimensionB === "string") {
        return dimensionA.localeCompare(dimensionB);
      }

      return (dimensionB as number) - (dimensionA as number);
    });

export const getTopContributorsChartActions = (
  yAxisOptions: SelectOption[],
  exposures: SelectOption[],
  exposureBuckets: SelectOption[],
  selectedExposure: string,
  t: TFunction
): ChartAction[] =>
  [
    {
      id: "y",
      title: t("common:analytics.y_axis"),
      type: "dropdownSelect",
      options: yAxisOptions,
      defaultOptionId: getTotalEventsOptionId(yAxisOptions),
    },
    {
      id: "exposure",
      title: t("common:analytics.exposure"),
      type: "dropdownSelect",
      options: exposures,
      defaultOptionId: selectedExposure,
    },
    selectedExposure !== NONE_EXPOSURE && {
      id: "exposureBucket",
      title: t("common:analytics.less_than_or_equal_to"),
      type: "dropdownSelect",
      options: exposureBuckets,
    },
    {
      id: "logarithmic",
      title: t("common:analytics.logarithmic_scale"),
      type: "boolean",
      defaultOptionId: "false",
    },
  ].filter(Boolean) as ChartAction[];

export const getDefaultTopContributorsExposure = (
  pageKey: string,
  availableExposures: SelectOption[]
) => {
  const predefinedChartActions = localStorage.getItem(pageKey);
  if (!predefinedChartActions) {
    return NONE_EXPOSURE;
  }

  const availableExposureIds = availableExposures.map(
    ({ id }) => id
  ) as string[];

  try {
    const parsedChartActions = JSON.parse(
      predefinedChartActions
    ) as SelectedChartOptions[];
    if (parsedChartActions.length === 0) {
      return NONE_EXPOSURE;
    }

    const selectedExposure = parsedChartActions.find(
      ({ id }) => id === "exposure"
    );
    if (selectedExposure) {
      const exposureId = selectedExposure.optionId as string;

      return availableExposureIds.includes(exposureId)
        ? exposureId
        : NONE_EXPOSURE;
    }
  } catch {
    console.error(
      `Could not parse local storage key '${pageKey}', value: ${predefinedChartActions}`
    );

    return NONE_EXPOSURE;
  }

  return NONE_EXPOSURE;
};

export const getDefaultTopContributorChartActions = (
  attributes: EntityAttribute[] | undefined,
  pageKey: string,
  yAxisOptions: SelectOption[],
  exposures: SelectOption[],
  t: TFunction
): ChartAction[] => {
  const defaultExposure = getDefaultTopContributorsExposure(pageKey, exposures);
  const defaultBuckets = mapByVehicleAgeExposureBuckets(
    attributes,
    defaultExposure
  );

  return getTopContributorsChartActions(
    yAxisOptions,
    exposures,
    defaultBuckets,
    defaultExposure,
    t
  );
};

export const getTopContributorsChartTitle = (
  actions: ChartAction[],
  selectedOptions: SelectedChartOptions[],
  exposure?: string,
  exposureBucket?: number
) => {
  const yAxisKey = getSelectedOptionId(selectedOptions, "y");
  const yAxisLabel = getAxisLabel(actions, "y", yAxisKey);
  const byVehicleAgeExposureValue = exposure
    ? getAxisValue(actions, "exposure", exposure)
    : "";
  const titleSuffix = exposure
    ? `by ${exposureBucket} ${byVehicleAgeExposureValue}`
    : "";

  return [yAxisLabel, titleSuffix].filter(Boolean).join(" ");
};

export const getTablePageKey = (pageKey: string) => `${pageKey}_table`;

export const isValidUrl = (url: string) => {
  try {
    new URL(url);

    return true;
  } catch {
    return false;
  }
};

export const mapByVehicleAgeExposureAttributes = (
  attributes: EntityAttribute[] | undefined,
  t: TFunction
): SelectOption[] => {
  if (!attributes || attributes.length === 0) {
    const monthsInService = t("common:months_in_service");

    return [
      { id: DEFAULT_EXPOSURE, value: monthsInService, label: monthsInService },
    ];
  }

  return attributes
    ?.filter((attr) => attr.byVehicleAgeExposure)
    .map(
      ({ ID, displayName }) =>
        ({
          id: ID,
          value: displayName,
          testId: `top-contrib-exposure-${ID}`,
        }) as SelectOption
    )
    .sort(sortByValue);
};

// TODO: we shouldn't hardcode MONTHS_IN_SERVICE_EXPOSURE_BUCKET when no
// attributes are present as monthsInService attribute is not guaranteed to exist.
export const mapByVehicleAgeExposureBuckets = (
  attributes: EntityAttribute[] | undefined,
  byVehicleAgeExposure: string
): SelectOption[] => {
  if (!attributes || attributes.length === 0) {
    return [MONTHS_IN_SERVICE_EXPOSURE_BUCKET];
  }

  const ageExposureBuckets =
    attributes.find((attr) => attr.ID === byVehicleAgeExposure)
      ?.byVehicleAgeExposureBuckets || [];

  return ageExposureBuckets.map(
    (bucket) =>
      ({
        id: bucket,
        value: bucket,
        testId: `top-contrib-exposureBucket-${bucket}`,
      }) as SelectOption
  );
};

// Returns the initial selected options for any chart actions from the filter sort state.
// It merges saved options from the filter sort state with default options, so there is backwards
// compatibility when new actions are added to the chart options.
export const getInitialSelectedChartOptions = (
  chartSettings: PageChartSettingsState | undefined,
  tabKey: string,
  chartOptionsKey: string,
  initialActions: ChartAction[]
) => {
  const savedOptions =
    chartSettings &&
    chartSettings[tabKey] &&
    chartSettings[tabKey][chartOptionsKey];

  const defaultOptions = getDefaultActions(initialActions);

  if (!savedOptions) return defaultOptions;

  // map of action IDs to their default values
  const defaultOptionsMap = Object.fromEntries(
    defaultOptions.map((option) => [option.id, option])
  );

  // map of saved options with validation
  const savedOptionsMap = Object.fromEntries(
    savedOptions.map((option) => {
      const { id, optionId } = option;

      const action = initialActions.find((action) => action.id === id);

      if (action?.type === "dropdownSelect" && action.options) {
        const isValidOption = action.options.some((opt) => opt.id === optionId);

        if (!isValidOption) {
          return [id, defaultOptionsMap[id] || option];
        }
      }

      // keep the saved option
      return [id, option];
    })
  );

  // merge maps, preferring saved options but including default options for any missing entries
  const mergedOptionsMap = { ...defaultOptionsMap, ...savedOptionsMap };

  return Object.values(mergedOptionsMap);
};
const UNSUPPORTED_PATCH_FIELDS = new Set<string>(["acl"]);
export const removeFalsyFromRecord = (
  obj: Record<string, any>,
  action: ActionType
) => {
  let newObj: Record<string, any> = {};
  Object.keys(obj)
    .filter((k) => action !== "update" || !UNSUPPORTED_PATCH_FIELDS.has(k))
    .forEach((key) => {
      if (obj[key]) {
        newObj[key] = obj[key];
      }

      if (!obj[key] && action === "update") {
        newObj[key] = null;
      }
    });

  return newObj;
};
